import {dataTestId} from '@hconnect/uikit'
import {MyLocation} from '@mui/icons-material'
import {Box, Card, CardContent, Stack, Typography, useTheme} from '@mui/material'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {useAllStrengthMeasurementUnits} from '../hooks/useStrengthMeasurementUnit'
import {useStrengthTargetOverviewUtils} from '../hooks/useStrengthTargetOverviewUtils'
import {DayPeriod, StrengthTarget} from '../types'

interface StrengthTargetOverviewMobileCardsProps {
  targets: StrengthTarget[]
  timezone: string
  dayPeriods: DayPeriod[]
}

export const StrengthTargetOverviewMobileCards: React.FC<
  StrengthTargetOverviewMobileCardsProps
> = ({targets, timezone, dayPeriods}) => {
  const {t} = useTranslation()
  const theme = useTheme()

  const units = useAllStrengthMeasurementUnits()
  const {getCurrentTargetForPeriod, formatDate, formatValidFrom} = useStrengthTargetOverviewUtils(
    targets,
    timezone
  )

  return (
    <Stack spacing={2} {...dataTestId('overview_mobile_cards')}>
      {dayPeriods.map((dayPeriod) => {
        const currentTarget = getCurrentTargetForPeriod(dayPeriod)
        const unit = units[dayPeriod]

        return (
          <Card
            key={dayPeriod}
            sx={{
              border: currentTarget ? `1px solid ${theme.palette.divider}` : 'none',
              backgroundColor: currentTarget
                ? theme.palette.background.paper
                : theme.palette.grey[50]
            }}
            {...dataTestId(`strength_target_overview_card_${dayPeriod}`)}
          >
            <CardContent>
              <Typography
                variant="subtitle1"
                sx={{fontWeight: 600, mb: 2}}
                {...dataTestId(`overview_card_day_${dayPeriod}`)}
              >
                {t('materialsSettings.strengthTargets.dayPeriod', {count: dayPeriod})}
              </Typography>

              {currentTarget ? (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 2
                  }}
                >
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{color: theme.palette.text.secondary, display: 'block', mb: 0.5}}
                    >
                      {t('materialsSettings.strengthTargets.currentTarget')}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <MyLocation
                        fontSize="small"
                        sx={{color: theme.palette.primary.main}}
                        {...dataTestId(`overview_current_icon_mobile_${dayPeriod}`)}
                      />
                      <Typography variant="body2" sx={{fontWeight: 600}}>
                        {currentTarget.target !== undefined
                          ? `${currentTarget.target}${unit ? ` ${unit}` : ''}`
                          : '-'}
                      </Typography>
                    </Stack>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      sx={{color: theme.palette.text.secondary, display: 'block', mb: 0.5}}
                    >
                      {t('materialsSettings.strengthTargets.lowerBound')}
                    </Typography>
                    <Typography variant="body2" sx={{fontWeight: 500}}>
                      {currentTarget.lowerBound !== undefined
                        ? `${currentTarget.lowerBound}${unit ? ` ${unit}` : ''}`
                        : '-'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      sx={{color: theme.palette.text.secondary, display: 'block', mb: 0.5}}
                    >
                      {t('materialsSettings.strengthTargets.upperBound')}
                    </Typography>
                    <Typography variant="body2" sx={{fontWeight: 500}}>
                      {currentTarget.upperBound !== undefined
                        ? `${currentTarget.upperBound}${unit ? ` ${unit}` : ''}`
                        : '-'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      sx={{color: theme.palette.text.secondary, display: 'block', mb: 0.5}}
                    >
                      {t('materialsSettings.strengthTargets.validFrom')}
                    </Typography>
                    <Typography variant="body2" sx={{fontWeight: 500}}>
                      {formatValidFrom(currentTarget.validFrom)}
                    </Typography>
                  </Box>

                  <Box sx={{gridColumn: '1 / -1'}}>
                    <Typography
                      variant="caption"
                      sx={{color: theme.palette.text.secondary, display: 'block', mb: 0.5}}
                    >
                      {t('materialsSettings.strengthTargets.validTo')}
                    </Typography>
                    <Typography variant="body2" sx={{fontWeight: 500}}>
                      {currentTarget.validTo ? formatDate(currentTarget.validTo) : '-'}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Typography
                  variant="body2"
                  sx={{color: theme.palette.text.secondary, fontStyle: 'italic'}}
                >
                  {t('materialsSettings.strengthTargets.noCurrentTarget')}
                </Typography>
              )}
            </CardContent>
          </Card>
        )
      })}
    </Stack>
  )
}
